<script setup>
import { SENTIMENT_BY_CODE } from "@/lib/chartPalettes";
import { ref, watch } from "vue";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/episto-ui/loading-state";
import useMyFetch from "@stores/useMyFetch";
import { captureEvent } from "@/lib/analytics";

const props = defineProps({
  open: { type: Boolean, default: false },
  questionId: { type: Number, required: true },
  sourceAnswerId: { type: Number, required: true },
  verbatimText: { type: String, default: "" },
  withSentiment: { type: Boolean, default: false },
});

const emit = defineEmits(["update:open", "saved"]);

const themes = ref([]);
const loading = ref(false);
const saving = ref(false);

const SENTIMENT_OPTIONS = Object.entries(SENTIMENT_BY_CODE).map(([value, { color, label }]) => ({
  value: Number(value),
  color,
  label,
}));

watch(
  () => props.open,
  async (isOpen) => {
    if (!isOpen) return;
    loading.value = true;
    try {
      const { data } = await useMyFetch(
        `/questions/${props.questionId}/verbatim_themes?source_answer_id=${props.sourceAnswerId}`,
      ).get().json();
      themes.value = (data.value?.themes || []).map((t) => ({ ...t }));
    } catch (e) {
      console.error("Failed to load verbatim themes", e);
    }
    loading.value = false;
  },
  { immediate: true },
);

function toggleTheme(index) {
  themes.value[index].enabled = !themes.value[index].enabled;
  if (themes.value[index].enabled && !themes.value[index].sentiment) {
    themes.value[index].sentiment = 2;
  }
}

function setSentiment(index, value) {
  themes.value[index].sentiment = value;
}

async function save() {
  saving.value = true;
  try {
    const { response } = await useMyFetch(`/questions/${props.questionId}/verbatim_themes`, {
      headers: { "Content-Type": "application/json" },
    })
      .put({
        source_answer_id: props.sourceAnswerId,
        themes: themes.value,
      })
      .json();
    if (response.value.ok) captureEvent("verbatim_themes_edited", { question_id: props.questionId });

    emit("saved");
    emit("update:open", false);
  } catch (e) {
    console.error("Failed to save verbatim themes", e);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Edit themes</DialogTitle>
      </DialogHeader>

      <p class="text-13 text-muted-foreground italic mb-4 line-clamp-2">
        "{{ verbatimText }}"
      </p>

      <LoadingState v-if="loading" size="sm" class="py-8" />

      <div v-else class="max-h-[50vh] overflow-y-auto">
        <div class="flex flex-col gap-1">
          <div
            v-for="(theme, index) in themes"
            :key="theme.code"
            class="flex items-center gap-3 py-1.5">
            <div class="flex items-center gap-2.5 flex-1 min-w-0">
              <Checkbox
                :model-value="theme.enabled"
                @update:model-value="toggleTheme(index)" />
              <span class="text-13 truncate">{{ theme.title }}</span>
            </div>
            <div v-if="withSentiment" class="flex items-center gap-1.5 shrink-0 w-[72px] justify-end">
              <template v-if="theme.enabled">
                <button
                  v-for="opt in SENTIMENT_OPTIONS"
                  :key="opt.value"
                  class="h-5 w-5 rounded-full border-2 transition-colors"
                  :style="{
                    borderColor: opt.color,
                    backgroundColor: theme.sentiment === opt.value ? opt.color : 'transparent',
                  }"
                  :title="opt.label"
                  @click="setSentiment(index, opt.value)" />
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-4">
        <Button variant="outline" @click="emit('update:open', false)">Cancel</Button>
        <Button :disabled="saving" @click="save">
          {{ saving ? "Saving..." : "Save" }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
